import React from 'react';
import styled, { keyframes } from 'styled-components';
import { getTechIcon } from './techIcon';
import { profile } from '../../data/portfolio';

/**
 * Decorative-but-interactive hero visual: concentric rings of tech icons
 * orbiting a glowing core. Rings spin; icons counter-spin to stay upright.
 * Hovering the visual pauses the orbit and lets you inspect each tech.
 */
const spin = keyframes` to { transform: rotate(360deg); } `;
const spinRev = keyframes` to { transform: rotate(-360deg); } `;
const pulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99,102,241,0.35); }
  50% { transform: scale(1.05); box-shadow: 0 0 46px 6px rgba(168,85,247,0.35); }
`;

const Stage = styled.div`
  position: relative;
  width: min(420px, 80vw);
  aspect-ratio: 1;
  display: grid;
  place-items: center;
  margin: 0 auto;
`;

const DashRing = styled.div`
  position: absolute;
  border-radius: 50%;
  border: 1px dashed var(--border-strong);
  inset: ${(p) => p.$inset}%;
`;

const Ring = styled.div`
  position: absolute;
  inset: ${(p) => p.$inset}%;
  border-radius: 50%;
  animation: ${(p) => (p.$rev ? spinRev : spin)} ${(p) => p.$dur}s linear infinite;
  ${Stage}:hover & { animation-play-state: paused; }
`;

const Node = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  transform: rotate(${(p) => p.$angle}deg) translateY(${(p) => p.$radius}px);
`;

const Chip = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 15px;
  border: 1px solid var(--border);
  background: var(--elev-bg);
  color: var(--primary-light);
  font-size: 1.4rem;
  box-shadow: var(--card-shadow);
  animation: ${(p) => (p.$rev ? spin : spinRev)} ${(p) => p.$dur}s linear infinite;
  transition: transform 0.25s ease, color 0.25s ease, border-color 0.25s ease;
  cursor: default;
  ${Stage}:hover & { animation-play-state: paused; }
  &:hover {
    color: var(--secondary);
    border-color: var(--primary-light);
    transform: translate(-50%, -50%) scale(1.18);
    z-index: 3;
  }
`;

const Core = styled.div`
  position: relative;
  z-index: 2;
  display: grid;
  place-items: center;
  width: 96px;
  height: 96px;
  border-radius: 26px;
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 2rem;
  color: #fff;
  background: var(--gradient-accent);
  animation: ${pulse} 4s ease-in-out infinite;
`;

const outer = ['React', 'Java', 'Spring Boot', 'PHP', 'Flutter', 'Docker'];
const inner = ['JavaScript', 'Python', 'Git', 'SQL'];

const layout = (items, radius, dur, rev) =>
  items.map((tech, i) => {
    const angle = (360 / items.length) * i;
    return (
      <Node key={tech} $angle={angle} $radius={radius}>
        <Chip $dur={dur} $rev={rev} title={tech}>
          {getTechIcon(tech)}
        </Chip>
      </Node>
    );
  });

const TechOrbit = () => (
  <Stage aria-hidden="true">
    <DashRing $inset={4} />
    <DashRing $inset={26} />

    <Ring $inset={4} $dur={30}>
      {layout(outer, -195, 30, false)}
    </Ring>
    <Ring $inset={26} $dur={22} $rev>
      {layout(inner, -128, 22, true)}
    </Ring>

    <Core>{profile.initials}</Core>
  </Stage>
);

export default TechOrbit;
